import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { t } from "@/lib/i18n";
import { fmtDate, timeAgo, initials, roleBadge } from "@/lib/format";
import Breadcrumbs from "@/components/Breadcrumbs";
import BadgesRow from "@/components/BadgesRow";
import { MessageCircle, Mail, MailX } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Profile() {
  const { username } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/users/${username}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [username]);

  if (loading) return <Skeleton className="h-64 w-full" />;
  if (!data) return <div>User not found</div>;

  const u = data.user;
  const badge = roleBadge(u.role);
  const isMe = user && user.id === u.id;
  const canPM = user && !isMe && u.pm_enabled !== false;

  const startPM = async () => {
    const content = window.prompt(`${u.username} کو پہلا پیغام لکھیے:`);
    if (!content) return;
    try {
      const { data: d } = await api.post("/pm/start", { to_username: u.username, content });
      toast.success("پیغام بھیج دیا");
      nav(`/messages/${d.conversation_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "پیغام نہیں بھیج سکا");
    }
  };

  return (
    <div className="space-y-6" data-testid="profile-page">
      <Breadcrumbs items={[{ label: t.home, to: "/" }, { label: u.username }]} />
      <Card className="p-6 flex flex-col sm:flex-row-reverse items-center sm:items-start gap-5">
        <Avatar className="h-24 w-24 border-2 border-primary/30">
          <AvatarImage src={u.avatar_url || undefined} />
          <AvatarFallback className="bg-primary/10 text-primary font-eng font-bold text-2xl">{initials(u.username)}</AvatarFallback>
        </Avatar>
        <div className="flex-1 text-center sm:text-right space-y-2">
          <div className="flex items-center gap-2 justify-center sm:justify-start">
            <h1 className="font-eng font-bold text-2xl text-primary">{u.username}</h1>
            {badge && <span className={`text-xs font-eng font-semibold px-2 py-0.5 rounded ${badge.cls}`}>{badge.label}</span>}
            {u.is_banned && <span className="text-xs font-eng font-semibold px-2 py-0.5 rounded bg-destructive text-destructive-foreground">Banned</span>}
          </div>
          <div className="text-sm font-eng text-muted-foreground">
            {t.joined}: {fmtDate(u.created_at)} · {u.post_count} {t.postCount}
          </div>
          <BadgesRow badges={u.badges} size="lg" />
          {u.bio && <p className="nastaliq mt-3">{u.bio}</p>}
          {user && !isMe && (
            <div className="flex gap-2 justify-center sm:justify-start pt-2">
              {canPM ? (
                <Button size="sm" onClick={startPM} className="bg-primary gap-1" data-testid="profile-pm">
                  <Mail size={14} /> <span className="urdu-naskh">نجی پیغام</span>
                </Button>
              ) : (
                <Button size="sm" variant="outline" disabled className="gap-1">
                  <MailX size={14} /> <span className="urdu-naskh">پیغامات بند</span>
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>

      <div>
        <h2 className="urdu-naskh text-xl font-semibold text-primary border-b-2 border-primary pb-1 mb-3">تازہ سرگرمی</h2>
        <Card className="divide-y divide-border">
          {data.recent_posts.length === 0 && (
            <div className="p-6 text-center urdu-naskh text-muted-foreground">— {t.noPosts}</div>
          )}
          {data.recent_posts.map((p) => (
            <Link
              key={p.id}
              to={`/t/${p.thread_id}#post-${p.id}`}
              className="block p-4 hover:bg-muted/40 forum-row"
              data-testid={`profile-post-${p.id}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <MessageCircle size={14} className="text-primary/70" />
                <span className="urdu-naskh text-primary">{p.thread_title}</span>
                <span className="text-xs text-muted-foreground font-eng me-auto">{timeAgo(p.created_at)}</span>
              </div>
              <div className="post-content line-clamp-2 text-sm opacity-80" dangerouslySetInnerHTML={{ __html: p.content }} />
            </Link>
          ))}
        </Card>
      </div>
    </div>
  );
}
